// InvitationsSection.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { useApplication } from './hooks/useApplication';
import DeclineInvitationModal from './DeclineInvitationModal';

const InvitationsSection = ({ applicationData, mode, applicationId, onOpenDeclineModal }) => {
  const { declineInvitation, statuses, statusesLoading, statusesError, fetchApplicationStatuses } = useApplication(applicationId);
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);


    useEffect(() => {
      if (applicationId) {
        fetchApplicationStatuses();
      }
    }, [applicationId, fetchApplicationStatuses]);

  

  // Фильтруем статусы связанные с приглашениями
  const invitationStatuses = statuses?.filter(item => 
    ['invite-sent', 'invite-accepted', 'invite-declined'].includes(item.name)
  ) || [];

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('uk-UA');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'invite-sent': { class: 'status-pending', label: 'Надіслано' },
      'invite-accepted': { class: 'status-success', label: 'Прийнято' },
      'invite-declined': { class: 'status-error', label: 'Відхилено' }
    };
    
    const config = statusConfig[status] || statusConfig['invite-sent'];
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const handleMenuToggle = (index, e) => {
    e.stopPropagation();
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = async (actionId, invitation) => {
    if (actionId === 'decline') {
      setSelectedInvitation(invitation);
      
      if (onOpenDeclineModal) {
        // Используем родительский колбэк для открытия модального окна
        onOpenDeclineModal(
          (reason) => handleDeclineInvitation(reason, invitation),
          invitation
        );
      } else {
        // Локальное открытие модального окна
        setIsDeclineModalOpen(true);
      }
    }
    setActiveMenuIndex(null);
  };

  const handleDeclineInvitation = async (reason, invitation = selectedInvitation) => {
    try {
      setLoading(true);
      await declineInvitation(
        reason, 
        invitation.kindergartenId, 
        invitation.groupNumber
      );
      
      setIsDeclineModalOpen(false);
      setSelectedInvitation(null);
      // Обновляем данные
      fetchApplicationStatuses();
    } catch (error) {
      console.error('Error declining invitation:', error);
      alert('Помилка при відхиленні запрошення');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsDeclineModalOpen(false);
    setSelectedInvitation(null);
  };

  const ActionsMenu = ({ onActionSelect, onClose, invitation, currentStatus }) => {
    if (!['invite-sent', 'invite-accept'].includes(currentStatus)) {
      return null;
    }

    if (!['invite-sent', 'invite-accept'].includes(invitation.name)) {
      return null;
    }

    return (
      <div className="actions-dropdown">
        <button
          className="dropdown-item d-flex align-items-center gap-2 p-2"
          onClick={() => onActionSelect('decline', invitation)}
          style={{
            border: 'none',
            backgroundColor: 'transparent',
            width: '100%',
            textAlign: 'left',
            fontSize: '14px',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          <FontAwesomeIcon icon={faTimesCircle} style={{ width: '16px', color: '#dc3545' }} />
          <span style={{ color: '#212529' }}>Відхилити</span>
        </button>
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  if (statusesLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Завантаження...</span>
        </div>
        <p className="mt-2">Завантаження запрошень...</p>
      </div>
    );
  }

  if (statusesError) {
    return (
      <div className="alert alert-danger m-3">
        <strong>Помилка завантаження запрошень:</strong> {statusesError}
        <div className="mt-2">
          <button className="btn btn-sm btn-outline-danger" onClick={fetchApplicationStatuses}>
            Спробувати знову
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="invitations-section">
      <h3 className="section-title">Надіслані запрошення</h3>
      
      <div className="table-responsive">
        <table className="history-table">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Дата та час</th>
              <th style={{ width: '15%' }}>Статус</th>
              <th style={{ width: '25%' }}>Заклад</th>
              <th style={{ width: '15%' }}>Група</th>
              {false && <th style={{ width: '25%' }}>Дії</th>}
            </tr>
          </thead>
          <tbody>
            {invitationStatuses.map((invitation, i) => {
              const index = "i_" + i;
              
              return (
                <tr key={invitation._id || index}>
                  <td>{formatDateTime(invitation.date)}</td>
                  <td>{getStatusBadge(invitation.name)}</td>
                  <td>
                    {invitation.kindergartenName ? 
                      `${invitation.kindergartenName} (${invitation.kindergartenNumber})` : 
                      invitation.kindergartenId
                    }
                    {invitation.kindergartenDistrict && (
                      <div><small className="text-muted">{invitation.kindergartenDistrict}</small></div>
                    )}
                  </td>
                  <td>{invitation.groupNumber}</td>
                  {false && <td className="text-end">
                    <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        className="btn btn-sm btn-link text-decoration-none p-0"
                        onClick={(e) => handleMenuToggle(index, e)}
                        style={{ color: '#0d6efd' }}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      
                      {activeMenuIndex == index && (
                        <ActionsMenu 
                          onActionSelect={handleActionSelect}
                          onClose={handleMenuClose}
                          invitation={invitation}
                          currentStatus={applicationData.currentStatus}
                        />
                      )}
                    </div>
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {invitationStatuses.length === 0 && (
        <div className="no-data">
          Запрошення відсутні
        </div>
      )}

      {/* Локальное модальное окно (используется если нет колбэка от родителя) */}
      {!onOpenDeclineModal && (
        <DeclineInvitationModal
          isOpen={isDeclineModalOpen}
          onClose={handleCloseModal}
          onDecline={handleDeclineInvitation}
          loading={loading}
          invitation={selectedInvitation}
          applicationData={applicationData}
        />
      )}
    </div>
  );
};

export default InvitationsSection;